<template>
  <div class="common-icon-button-wrapper">
    <button
      class="common-icon-button"
      :class="[color, disabled ? 'disabled' : '', squared ? 'squared' : '']"
    >
      <CommonIcon class="icon" v-if="icon" :name="icon" :stroke-width="1.5" :size="20" />
    </button>
    <p class="label" v-if="label">{{ label }}</p>
    <div v-if="hover" class="hover">{{ hover }}</div>
  </div>
</template>

<script setup lang="ts">
import CommonIcon, { type Icons } from './CommonIcon.vue'

const props = withDefaults(
  defineProps<{
    color?: 'primary' | 'dark' | 'success' | 'warning' | 'danger' | 'light'
    icon: Icons
    disabled?: boolean
    squared?: boolean
    label?: string
    hover?: string
  }>(),
  {
    color: 'primary'
  }
)
</script>

<style scoped lang="scss">
.common-icon-button-wrapper {
  position: relative;
  display: flex;
  gap: 10px;
  cursor: pointer;
  align-items: center;
}

.hover {
  pointer-events: none;
  transition: 600ms cubic-bezier(0.19, 1, 0.22, 1);
  opacity: 0;
  position: absolute;
  white-space: nowrap;
  top: -20px;
  left: 50%;
  color: var(--vc-dark);
  padding: 5px;
  transform: translateX(-50%);
  border-radius: 5px;
}

.common-icon-button-wrapper:hover > .hover {
  transition: 600ms cubic-bezier(0.19, 1, 0.22, 1);
  opacity: 0.3;
  top: -30px;
}

.label {
  color: var(--vc-dark);
  font-weight: 400;
  font-size: 14px;
  white-space: nowrap;
}

.common-icon-button {
  padding: 8px;
  border-radius: 50%;
  border: unset;
  color: white;
  background-color: var(--vc-primary);
  cursor: pointer;
}

.icon {
  display: block;
}

.light {
  color: var(--vc-dark);
  opacity: 0.5;
}

.disabled {
  opacity: 0.5;
  cursor: default;
  pointer-events: none;
}

.squared {
  border-radius: 2px;
  background-color: unset !important;
  color: var(--vc-dark);
  border: 1px solid rgba(var(--vcrgb-dark), 0.2);
  padding: 15px;
}

.common-icon-button-wrapper:hover > .squared {
  color: var(--vc-white);
}

@mixin color($class: primary) {
  $background: var(--vc-#{$class});
  $background-rgb: var(--vcrgb-#{$class});

  .#{$class} {
    background-color: $background;
  }

  .common-icon-button-wrapper:hover > .#{$class} {
    transition: 600ms cubic-bezier(0.19, 1, 0.22, 1);
    background-color: color-mix(in srgb, $background, #000 5%) !important;
  }
}

@include color(primary);
@include color(dark);
@include color(success);
@include color(warning);
@include color(danger);
@include color(light);
</style>
